/**
 * Unified Analytics utility that tracks events to both Meta Pixel and Google Analytics
 * This provides a single interface for all analytics tracking
 */

import { trackPageView as trackMetaPixelPageView, trackViewContent, trackAddToCart as trackMetaPixelAddToCart, trackInitiateCheckout as trackMetaPixelInitiateCheckout, trackPurchase as trackMetaPixelPurchase } from './metaPixel';
import { trackPageView as trackGAPageView, trackViewItem, trackAddToCart as trackGAAddToCart, trackBeginCheckout, trackPurchase as trackGAPurchase } from './googleAnalytics';
import { isAnalyticsSuppressedPath } from './analyticsRoutes';

function currentPathname() {
  return typeof window !== 'undefined' && window.location ? window.location.pathname : '';
}

/**
 * Some routes carry a bearer token in their URL (see analyticsRoutes.js), and
 * both providers stamp the live document URL onto every hit they send. On a
 * cold load of such a route public/index.html neither loads gtag.js nor calls
 * fbq('init'). Once the address bar holds an ordinary route again, perform
 * that deferred initialization here - with GA4's automatic page_view off,
 * since trackPageView reports the page itself. gtag.js is loaded only now so
 * that it never sees the token URL (it would report it as the referrer of
 * later page views).
 *
 * Returns false while the address bar still holds a suppressed route, in
 * which case nothing may be sent.
 */
function prepareTracking() {
  if (isAnalyticsSuppressedPath(currentPathname())) {
    return false;
  }

  if (typeof window !== 'undefined' && window.__MORIA_SUPPRESS_ANALYTICS__) {
    const ids = window.__MORIA_ANALYTICS_IDS__ || {};
    if (ids.ga && typeof window.gtag === 'function') {
      if (typeof window.__MORIA_LOAD_GTAG__ === 'function') {
        window.__MORIA_LOAD_GTAG__();
      }
      window.gtag('js', new Date());
      window.gtag('config', ids.ga, { send_page_view: false });
    }
    if (ids.pixel && typeof window.fbq === 'function') {
      window.fbq('init', ids.pixel);
    }
    window.__MORIA_SUPPRESS_ANALYTICS__ = false;
  }

  return true;
}

/**
 * Analytics class that provides unified tracking for Meta Pixel and Google Analytics
 */
class Analytics {
  /**
   * Initialize analytics
   * Note: Meta Pixel is initialized in index.html, so this is a no-op
   */
  static init() {
    // Meta Pixel is initialized in index.html, no action needed
  }

  /**
   * Track page view
   * @param {string} pagePath - Page path to track
   * @param {string} pageTitle - Optional page title
   */
  static trackPageView(pagePath, pageTitle) {
    // Check the reported path as well as the address bar (prepareTracking) -
    // reporting a clean path is not enough to keep the token out of the hit.
    if (isAnalyticsSuppressedPath(pagePath) || !prepareTracking()) {
      return;
    }

    trackMetaPixelPageView(pagePath);
    trackGAPageView(pagePath, pageTitle);
  }

  /**
   * Track product view (ViewContent for Meta Pixel, ViewItem for GA)
   * @param {Object} product - Product object with id, name, price, etc.
   */
  static trackViewProduct(product) {
    if (!prepareTracking()) return;
    trackViewContent(product);
    trackViewItem(product);
  }

  /**
   * Track add to cart event
   * @param {Object} product - Product object
   * @param {number} quantity - Quantity added to cart
   */
  static trackAddToCart(product, quantity = 1) {
    if (!prepareTracking()) return;
    trackMetaPixelAddToCart(product, quantity);
    trackGAAddToCart(product, quantity);
  }

  /**
   * Track checkout initiation
   * @param {number} value - Total cart value
   * @param {number} numItems - Number of items in cart
   * @param {Array} contents - Array of cart items
   */
  static trackInitiateCheckout(value, numItems, contents = []) {
    if (!prepareTracking()) return;
    trackMetaPixelInitiateCheckout(value, numItems, contents);
    trackBeginCheckout(value, numItems, contents);
  }

  /**
   * Track purchase event
   * @param {Object} orderData - Order data with id, value, currency, items
   */
  static trackPurchase(orderData) {
    if (!prepareTracking()) return;
    trackMetaPixelPurchase(orderData);
    trackGAPurchase(orderData);
  }
}

export default Analytics;

